/**
 * Missed-call calculator. Every figure is derived from the numbers the
 * visitor types. This module does not fetch, persist, or supply defaults.
 * The page may say "Your numbers stay in your browser" only because the
 * math runs here, in the browser, and nothing in this file leaves the page.
 */

import { formatPageUpdated } from "../seo/pageDates";

export const MISSED_CALL_CALCULATOR_PATH = "/tools/missed-call-calculator";
export const MISSED_CALL_CALCULATOR_URL = "https://flogpt.com/tools/missed-call-calculator";

/** Pacific publish day. Sitemap lastmod (`pageDates.ts`) and the visible "Last reviewed" line. */
export const REVIEWED_ON = "2026-11-03";
export const REVIEWED_LABEL = formatPageUpdated(REVIEWED_ON);

export const SEO_TITLE = "Missed Call Calculator: What Unanswered Calls Cost | floGPT";
export const SEO_DESCRIPTION =
    "Estimate what missed calls cost your business. Enter your own call volume, missed-call rate, close rate and job value. Free, no login.";

export const H1 = "Missed call calculator: what unanswered calls cost you";
export const INTRO =
    "Enter your own call numbers to see what missed calls may be costing you each month and each year. The calculator multiplies your missed calls by the share of callers who don't call back, your close rate and your average job value. Every number comes from you, and nothing is pre-filled. It's free, with no login.";

export const APPROVED_PRICE = "Free (100 messages/month), paid plans from $49/month";
export const PRIVACY_LINE = "Your numbers stay in your browser.";
export const EMPTY_STATE = "Fill in the first five fields to see your estimate.";
export const ESTIMATE_NOTE =
    "These figures are estimates based only on the numbers you entered. They aren't a prediction or a guarantee.";
export const ZERO_MISSED = "You're not missing any calls. Nice work.";
export const ADD_COSTS_PROMPT =
    "Add a plan cost and your expected call usage to see a figure after costs. Usage rates are on the pricing page.";

export const SIGNUP_PATH = "/signup?utm_source=tool&utm_medium=seo&utm_campaign=missed-call-calculator";
export const RESULTS_SIGNUP_PATH = `${SIGNUP_PATH}&utm_content=results`;

export const FORMULAS = [
    "Missed calls per month = inbound calls × calls you miss %",
    "Lost leads per month = missed calls × missed callers who don't call back %",
    "Lost revenue per month = lost leads × callers who become customers % × average customer value",
    "Lost revenue per year = lost revenue per month × 12",
    "Recoverable revenue per month = lost revenue per month × missed calls an AI voice agent would answer %",
    "After costs per month = recoverable revenue per month − plan cost − expected call usage",
] as const;

export const HOW_TO_LOWER = [
    {
        title: "Route calls before they ring out.",
        body: "Send calls to a second person or a shared line when the first one is busy, instead of straight to voicemail.",
    },
    {
        title: "Make voicemail a next step.",
        body: "Tell callers when you'll call back, and ask for the details you need to quote or book.",
    },
    {
        title: "Set a callback rule.",
        body: "Pick a time limit for returning missed calls and track it. The callback figure you enter above is a good starting measure.",
    },
] as const;

export const INDUSTRIES = [
    { href: "/use-cases/home-services", label: "Home services" },
    { href: "/use-cases/dental-healthcare", label: "Dental and healthcare" },
    { href: "/use-cases/legal", label: "Legal" },
    { href: "/use-cases/insurance", label: "Insurance" },
    { href: "/use-cases/real-estate", label: "Real estate" },
    { href: "/use-cases/auto-dealers", label: "Auto dealers" },
] as const;

export const WHO_USES_LINE =
    "Any business that books work over the phone can use the calculator. Enter your own numbers either way.";

export const CTA_HEADING = "Stop sending callers to voicemail";
export const CTA_BODY =
    "Set up an AI voice agent that answers inbound calls, and see every conversation in one CRM inbox.";
export const CTA_PRICE_NOTE = `${APPROVED_PRICE}. Calls and SMS are billed as usage on top of the plan.`;

export const METHODOLOGY = `How this calculator works. Every input comes from you, and the calculator doesn't add industry averages, benchmarks or third-party statistics. The math is the formulas shown above. The annual figure assumes every month looks like the one you entered, so it doesn't account for seasonality. The calculator doesn't model repeat business, customer lifetime value, or callers who reach you some other way (web form, email, a competitor). Recoverable revenue depends entirely on your own estimate in field 6. Results are rounded. ${PRIVACY_LINE} Published by FloGPT (Pulse Real LLC). Last reviewed ${REVIEWED_LABEL}.`;

const HOW_IT_WORKS_ANSWER =
    "It multiplies five numbers you enter. Missed calls are your monthly calls times the share you miss. Lost leads are missed calls times the share who don't call back. Lost revenue is lost leads times your close rate times your average customer value. The annual figure is the monthly figure times 12.";

export const FAQS: ReadonlyArray<{ question: string; answer: string }> = [
    {
        question: "How does the missed call calculator work?",
        answer: `${HOW_IT_WORKS_ANSWER} ${PRIVACY_LINE}`,
    },
    {
        question: "Why doesn't the calculator fill in typical numbers?",
        answer: "Call volume, missed-call rates, callback rates, close rates and job values vary widely from one business to the next, and we don't want to put a figure in front of you that isn't yours. Use your own phone records and sales numbers so the estimate reflects your business.",
    },
    {
        question: "Where do I find my missed call numbers?",
        answer: "Most phone systems and carriers have a call log or report that lists missed, unanswered or abandoned calls. Count a full month if you can. To estimate how many missed callers never call back, check which missed numbers don't appear again in your log within a few days.",
    },
    {
        question: "Is the recoverable revenue figure a guarantee?",
        answer: "No. It's an estimate built only from the numbers you enter, including your own guess at how many missed calls an AI voice agent would answer. Actual results depend on your callers, your offer and how you follow up. It doesn't include call usage costs unless you enter them.",
    },
    {
        question: "Does floGPT have a free plan?",
        answer: `Yes. floGPT pricing is ${APPROVED_PRICE}. Voice agents need a paid plan because they use real telephony, and calls and SMS are billed as usage on top of the plan. Current plan details and usage rates are on the floGPT pricing page.`,
    },
];

export type FieldKey = "calls" | "missPct" | "noCallbackPct" | "convPct" | "avgValue" | "aiPct" | "planCost" | "usage";

export type CalculatorInputs = Record<FieldKey, string>;

export const EMPTY_INPUTS: CalculatorInputs = {
    calls: "",
    missPct: "",
    noCallbackPct: "",
    convPct: "",
    avgValue: "",
    aiPct: "",
    planCost: "",
    usage: "",
};

type FieldKind = "whole" | "percent" | "money";

export interface FieldDef {
    key: FieldKey;
    label: string;
    placeholder: string;
    required: boolean;
    kind: FieldKind;
    error: string;
}

export const FIELDS: readonly FieldDef[] = [
    {
        key: "calls",
        label: "Inbound calls per month",
        placeholder: "Your phone system's call log",
        required: true,
        kind: "whole",
        error: "Enter a whole number of calls (0 or more).",
    },
    {
        key: "missPct",
        label: "Calls you miss (%)",
        placeholder: "After hours, busy lines, or calls sent to voicemail",
        required: true,
        kind: "percent",
        error: "Enter a percentage from 0 to 100.",
    },
    {
        key: "noCallbackPct",
        label: "Missed callers who don't call back (%)",
        placeholder: "Missed numbers that never show up again",
        required: true,
        kind: "percent",
        error: "Enter a percentage from 0 to 100.",
    },
    {
        key: "convPct",
        label: "Callers who become customers (%)",
        placeholder: "The share who end up booking or buying",
        required: true,
        kind: "percent",
        error: "Enter a percentage from 0 to 100.",
    },
    {
        key: "avgValue",
        label: "Average value of a new customer ($)",
        placeholder: "What a typical new job, booking or sale is worth",
        required: true,
        kind: "money",
        error: "Enter an amount of 0 or more.",
    },
    {
        key: "aiPct",
        label: "Missed calls an AI voice agent would answer (%), optional",
        placeholder: "Leave blank to skip the recoverable-revenue line",
        required: false,
        kind: "percent",
        error: "Enter a percentage from 0 to 100, or leave blank.",
    },
    {
        key: "planCost",
        label: "Plan cost per month ($), optional",
        placeholder: "The plan you'd use",
        required: false,
        kind: "money",
        error: "Enter an amount of 0 or more, or leave blank.",
    },
    {
        key: "usage",
        label: "Expected call usage per month ($), optional",
        placeholder: "Estimate yours from the usage rates on the pricing page",
        required: false,
        kind: "money",
        error: "Enter an amount of 0 or more, or leave blank.",
    },
];

const REQUIRED_KEYS: FieldKey[] = ["calls", "missPct", "noCallbackPct", "convPct", "avgValue"];

export interface CalculatorResult {
    calls: number;
    missPct: number;
    noCallbackPct: number;
    convPct: number;
    /** The amount entered, not a rounded dollar. */
    avgValue: number;
    missedCalls: number;
    lostLeads: number;
    lostRevMo: number;
    lostRevYr: number;
    aiPct: number | null;
    recoverableMo: number | null;
    recoverableYr: number | null;
    planCost: number | null;
    usageCost: number | null;
    netMo: number | null;
    /** Field 6 is a real percentage and field 7 or 8 was left blank. */
    costsIncomplete: boolean;
}

export type FieldErrors = Partial<Record<FieldKey, string>>;

const isBlank = (raw: string): boolean => raw.trim() === "";

/** Largest money amount whose product with a safe call count stays finite. */
const MAX_MONEY = Number.MAX_SAFE_INTEGER;

/**
 * Accept `1,000` and a trailing `%` on percentages. A comma is only a
 * thousands separator (`1,000` or `1,000.50`), not a decimal mark.
 */
function canonicalNumber(raw: string, allowPercent: boolean): string | null {
    let text = raw.trim();
    if (allowPercent && text.endsWith("%")) text = text.slice(0, -1).trim();
    if (text.includes(",")) {
        if (!/^\d{1,3}(?:,\d{3})+(?:\.\d+)?$/.test(text)) return null;
        text = text.replace(/,/g, "");
    }
    return text;
}

const parseWhole = (raw: string): number | null => {
    const text = canonicalNumber(raw, false);
    if (text === null || !/^\d+$/.test(text)) return null;
    const value = Number(text);
    if (!Number.isSafeInteger(value)) return null;
    return value;
};

const parseAmount = (raw: string): number | null => {
    const text = canonicalNumber(raw, false);
    if (text === null || !/^\d+(?:\.\d+)?$/.test(text)) return null;
    const value = Number(text);
    if (!Number.isFinite(value) || value < 0) return null;
    return Math.min(value, MAX_MONEY);
};

const parsePercent = (raw: string): number | null => {
    const text = canonicalNumber(raw, true);
    if (text === null || !/^\d+(?:\.\d+)?$/.test(text)) return null;
    const value = Number(text);
    if (!Number.isFinite(value) || value < 0 || value > 100) return null;
    return value;
};

const parseKind = (kind: FieldKind, raw: string): number | null => {
    if (kind === "whole") return parseWhole(raw);
    if (kind === "percent") return parsePercent(raw);
    return parseAmount(raw);
};

export function validateInputs(inputs: CalculatorInputs): FieldErrors {
    const errors: FieldErrors = {};
    for (const field of FIELDS) {
        const raw = inputs[field.key] ?? "";
        if (isBlank(raw)) {
            if (field.required) errors[field.key] = field.error;
            continue;
        }
        if (parseKind(field.kind, raw) === null) errors[field.key] = field.error;
    }
    return errors;
}

/**
 * Full precision. The page's formulas multiply the numbers entered; rounding
 * happens only in the formatters. Returns null until the five required
 * fields are valid. Optional blanks stay null; they are not treated as zero.
 */
export function calculateMissedCalls(inputs: CalculatorInputs): CalculatorResult | null {
    const errors = validateInputs(inputs);
    if (REQUIRED_KEYS.some((key) => errors[key])) return null;

    const calls = parseWhole(inputs.calls)!;
    const missPct = parsePercent(inputs.missPct)!;
    const noCallbackPct = parsePercent(inputs.noCallbackPct)!;
    const convPct = parsePercent(inputs.convPct)!;
    const avgValue = parseAmount(inputs.avgValue)!;

    const missedCalls = (calls * missPct) / 100;
    const lostLeads = (missedCalls * noCallbackPct) / 100;
    const lostRevMo = lostLeads * (convPct / 100) * avgValue;
    const lostRevYr = lostRevMo * 12;

    const aiBlank = isBlank(inputs.aiPct);
    const aiInvalid = !aiBlank && errors.aiPct !== undefined;
    const aiPct = !aiBlank && !aiInvalid ? parsePercent(inputs.aiPct) : null;
    const recoverableMo = aiPct === null ? null : lostRevMo * (aiPct / 100);
    const recoverableYr = recoverableMo === null ? null : recoverableMo * 12;

    const planBlank = isBlank(inputs.planCost);
    const usageBlank = isBlank(inputs.usage);
    const planInvalid = !planBlank && errors.planCost !== undefined;
    const usageInvalid = !usageBlank && errors.usage !== undefined;
    const planCost = !planBlank && !planInvalid ? parseAmount(inputs.planCost) : null;
    const usageCost = !usageBlank && !usageInvalid ? parseAmount(inputs.usage) : null;

    const netMo =
        recoverableMo !== null && planCost !== null && usageCost !== null
            ? recoverableMo - planCost - usageCost
            : null;

    const figures = [missedCalls, lostLeads, lostRevMo, lostRevYr, recoverableMo, recoverableYr, netMo];
    if (figures.some((value) => value !== null && !Number.isFinite(value))) return null;

    return {
        calls,
        missPct,
        noCallbackPct,
        convPct,
        avgValue,
        missedCalls,
        lostLeads,
        lostRevMo,
        lostRevYr,
        aiPct,
        recoverableMo,
        recoverableYr,
        planCost,
        usageCost,
        netMo,
        costsIncomplete: aiPct !== null && (planBlank || usageBlank),
    };
}

const wholeNumber = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

export function formatCount(value: number): string {
    return wholeNumber.format(value);
}

/** Whole dollars for display, thousands separators, no currency symbol. */
export function formatDollars(value: number): string {
    return wholeNumber.format(Math.abs(value));
}

/** The visitor's rate, without trailing zeros. */
export function formatRate(value: number): string {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(value);
}

export function formatSignedDollars(value: number): string {
    const body = formatDollars(value);
    return value < 0 ? `-$${body}` : `$${body}`;
}
